'use client';

/**
 * PromptInput — adapted from AI Elements (https://elements.ai-sdk.dev/components/prompt-input)
 *
 * Extremely simplified version keeping only core functionality:
 * - Textarea with Enter-to-submit
 * - Submit button with streaming status states
 * - Extensible footer/tools slots for custom buttons
 *
 * File attachments, command palette, tabs, hover cards, and provider system
 * were stripped. Re-add from the AI Elements registry when needed.
 *
 * TODO:
 * - check icons used in submit button
 */

import { Spinner } from '../../spinner/spinner';
import { cn } from '../../../lib/utils/cn';
import { Icon } from '../../icon/icon';
import { Button } from '../../button/button';
import { ArrowUp, Close, SquareSolid } from '@bridebook/aura/src/icons';
import type { ChatStatus } from './types';
import {
  useCallback,
  useRef,
  useState,
  type ReactNode,
  type FormEvent,
  type FormEventHandler,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type CSSProperties,
} from 'react';

// ============================================================================
// Helpers
// ============================================================================

const focusPromptInput = (e: React.MouseEvent<HTMLElement>) => {
  if ((e.target as HTMLElement).closest('button, a, [role="menuitem"]')) return;
  const root = (e.currentTarget.closest('[data-slot="input-group"]') ??
    e.currentTarget) as HTMLElement;
  (root.querySelector('textarea') ?? root.querySelector('input'))?.focus();
};

// ============================================================================
// PromptInput (Root Form)
// ============================================================================

export interface PromptInputMessage {
  text: string;
}

export type PromptInputProps = Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  onSubmit: (
    message: PromptInputMessage,
    event: FormEvent<HTMLFormElement>,
  ) => void | Promise<void>;
  classNames?: {
    inputGroup?: string;
  };
};

export const PromptInput = ({
  className,
  onSubmit,
  children,
  classNames,
  ...props
}: PromptInputProps) => {
  const formRef = useRef<HTMLFormElement | null>(null);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (event) => {
      event.preventDefault();

      const form = event.currentTarget;
      const formData = new FormData(form);
      const text = (formData.get('message') as string) || '';

      try {
        const result = onSubmit({ text }, event);

        if (result instanceof Promise) {
          try {
            await result;
            form.reset();
          } catch {
            // Don't reset on error
          }
        } else {
          form.reset();
        }
      } catch {
        // Don't reset on error
      }
    },
    [onSubmit],
  );

  return (
    <form className={cn('w-full', className)} onSubmit={handleSubmit} ref={formRef} {...props}>
      <div
        data-slot="input-group"
        role="group"
        onClick={focusPromptInput}
        className={cn(
          // layout
          'relative flex h-auto w-full flex-col items-center',
          // appearance
          'overflow-hidden rounded-lg bg-space-04 outline-none hover:bg-space-08',
          // animation
          'transition-[color,box-shadow]',
          // focus state
          'has-[[data-slot=input-group-control]:focus-visible]:bg-white has-[[data-slot=input-group-control]:focus-visible]:ring-2 has-[[data-slot=input-group-control]:focus-visible]:ring-indigo-crush',
          // group
          'group/input-group',
          //
          'transition-[background-color,color,box-shadow,border] duration-150 outline-none',
          classNames?.inputGroup,
        )}>
        {children}
      </div>
    </form>
  );
};

// ============================================================================
// Layout Components
// ============================================================================

export type PromptInputBodyProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputBody = ({ className, ...props }: PromptInputBodyProps) => (
  <div className={cn('contents', className)} {...props} />
);

export const PromptInputFooter = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div
    role="group"
    data-slot="prompt-input-footer"
    onClick={focusPromptInput}
    className={cn(
      // layout
      'flex h-auto w-full items-center justify-between gap-2 px-4 pt-1.5 pb-[10px]',
      // typography
      'text-sm font-medium text-space-60',
      // interaction
      'select-none',
      // icons
      '[&>svg:not([class*="size-"])]:size-4',
      // state
      'group-data-[disabled=true]/input-group:opacity-40',
      className,
    )}
    {...props}
  />
);

export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTools = ({ className, ...props }: PromptInputToolsProps) => (
  <div className={cn('flex min-w-0 items-center gap-1', className)} {...props} />
);

// ============================================================================
// Textarea
// ============================================================================

const TEXT_AREA_VERTICAL_PADDING = 10;
const TEXT_AREA_LINE_HEIGHT = 24;

export type PromptInputTextareaProps = React.ComponentProps<'textarea'> & {
  minLines?: 2 | 3 | 4;
};
export const PromptInputTextarea = ({
  onKeyDown,
  className,
  placeholder = 'What would you like to know?',
  minLines = 2 as const,
  ...props
}: PromptInputTextareaProps) => {
  const [isComposing, setIsComposing] = useState(false);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      onKeyDown?.(e);

      if (e.defaultPrevented) {
        return;
      }

      if (e.key === 'Enter') {
        if (isComposing || e.nativeEvent.isComposing) {
          return;
        }
        if (e.shiftKey) {
          return;
        }
        e.preventDefault();

        const { form } = e.currentTarget;
        const submitButton = form?.querySelector(
          'button[type="submit"]',
        ) as HTMLButtonElement | null;
        if (submitButton?.disabled) {
          return;
        }

        form?.requestSubmit();
      }
    },
    [onKeyDown, isComposing],
  );

  const handleCompositionEnd = useCallback(() => setIsComposing(false), []);
  const handleCompositionStart = useCallback(() => setIsComposing(true), []);

  return (
    <textarea
      data-slot="input-group-control"
      name="message"
      onCompositionEnd={handleCompositionEnd}
      onCompositionStart={handleCompositionStart}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      style={
        {
          '--line-height': `${TEXT_AREA_LINE_HEIGHT}px`,
          '--vertical-padding': `${TEXT_AREA_VERTICAL_PADDING}px`,
          '--min-height': `calc(${minLines * TEXT_AREA_LINE_HEIGHT + TEXT_AREA_VERTICAL_PADDING * 2}px)`,
        } as CSSProperties
      }
      className={cn(
        // layout: max height is 5 lines of text, min is 2 lines. Line height is 24px. Vertical padding is 10px.
        'max-h-[140px] min-h-[var(--min-height)] w-full flex-1 px-4 py-[var(--vertical-padding)]',
        // typography
        'text-left text-base leading-[var(--line-height)]',
        // appearance
        'resize-none rounded-none border-0 bg-transparent shadow-none outline-none',
        // placeholder & focus
        'placeholder:text-space-60 focus-visible:ring-0',
        // scroll
        'field-sizing-content scrollbar-thin',
        // color
        'text-space',
        className,
      )}
      {...props}
    />
  );
};

// ============================================================================
// Buttons
// ============================================================================

export type PromptInputSubmitProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  status?: ChatStatus;
  onStop?: () => void;
  children?: ReactNode;
};

export const PromptInputSubmit = ({
  className,
  status,
  onStop,
  onClick,
  children,
  ...props
}: PromptInputSubmitProps) => {
  const isGenerating = status === 'submitted' || status === 'streaming';

  let StatusIcon: ReactNode = <Icon icon={ArrowUp} className="size-4" />;

  if (status === 'submitted') {
    StatusIcon = <Spinner />;
  } else if (status === 'streaming') {
    StatusIcon = <Icon icon={SquareSolid} className="size-4" />;
  } else if (status === 'error') {
    StatusIcon = <Icon icon={Close} className="size-4" />;
  }

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isGenerating && onStop) {
        e.preventDefault();
        onStop();
        return;
      }
      onClick?.(e);
    },
    [isGenerating, onStop, onClick],
  );

  return (
    <Button
      aria-label={isGenerating ? 'Stop' : 'Submit'}
      type={isGenerating && onStop ? 'button' : 'submit'}
      onClick={handleClick}
      size="sm"
      variant="primary"
      className={cn('size-8 p-0', className)}
      {...props}>
      {children ?? StatusIcon}
    </Button>
  );
};
